import { ApolloClient } from "@apollo/client";
import { AgGrid, toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { GetBankAccountsForConfigurationDocument } from "#graphql/supergraphHooks";
import { type GetBankAccountsForConfigurationQuery } from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import { toLoadBankAccountsSuccessParams } from "./utils";

/**
 * This class is used by AG Grid to fetch bank accounts (paginated) via GQL
 * and pass them into the table.
 *
 * The bank accounts are passed into toLoadBankAccountsSuccessParams which transforms
 * the data into row objects that are passed into AGGrid. It also informs
 * AG Grid of the total number of rows so it knows when to stop paginating.
 *
 * The after property is used to store the cursor used to paginate through the bank accounts.
 */
export class BankAccountsGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private apiClient: ApolloClient<object>;
  private after: string;
  private rowCountPerPage: number;
  private arenaId: number;

  constructor(client: ApolloClient<object>, rowCountPerPage: number, arenaId: number) {
    this.apiClient = client;
    this.after = "";
    this.rowCountPerPage = rowCountPerPage;
    this.arenaId = arenaId;
  }

  /**
   * Updates the arenaId and resets the pagination cursor.
   * The cursor must be reset when changing arenaId because the cursor is specific
   * to the previous query result set. A new arenaId means a completely different
   * set of bank accounts, so pagination must start from the beginning.
   */
  public setArenaId = (arenaId: number): void => {
    this.arenaId = arenaId;
    this.after = "";
  };

  /**
   * Resets the pagination cursor to start from the beginning.
   * Should be called when refreshing data to ensure we get fresh results.
   */
  public resetCursor = (): void => {
    this.after = "";
  };

  public getRows = (params: AgGrid.IServerSideGetRowsParams): void => {
    const { api } = params;

    if (!this.arenaId) {
      params.success({ rowData: [], rowCount: 0 });
      api.showNoRowsOverlay();
      return;
    }

    api.showLoadingOverlay();

    this.apiClient
      .query<GetBankAccountsForConfigurationQuery>({
        query: GetBankAccountsForConfigurationDocument,
        context: clientContext(),
        variables: {
          arenaId: this.arenaId,
          first: this.rowCountPerPage,
          after: this.after || undefined,
        },
        fetchPolicy: "network-only",
      })
      .then((response) => {
        const successParams = toLoadBankAccountsSuccessParams(response.data);
        this.after = response.data.bankAccountsConnection?.pageInfo?.endCursor || "";

        if (successParams.resp.rowCount === 0) {
          api.showNoRowsOverlay();
        } else {
          api.hideOverlay();
        }

        params.success(successParams.resp);
      })
      .catch((e: unknown) => {
        addError(formatError(e));
        const errorMessage = e instanceof Error ? e.message : String(e);
        toast.error(`Fetching bank accounts failed: ${errorMessage}`);
        api.hideOverlay();
        params.fail();
      });
  };
}
